import type { ReactNode } from "react";
import { cn } from "./cn.js";
import { focusRing } from "./interactive.js";

export type SegmentedOption<T extends string> = {
  value: T;
  label: ReactNode;
  title?: string | undefined;
  disabled?: boolean | undefined;
};

type Size = "xs" | "sm" | "md";

const itemSize: Record<Size, string> = {
  xs: "h-5 px-1.5 text-[11px]",
  sm: "h-6 px-2 text-[11.5px]",
  md: "h-7 px-2.5 text-[12px]",
};

/** Segmented switcher — one of a group is selected (radio
 *  semantics). onChange is called even when the ACTIVE item is pressed — if
 *  toggle-off is needed the caller handles it itself (e.g. the VAR side pick). */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  mono,
  className,
}: {
  options: SegmentedOption<T>[];
  value: T | null;
  onChange: (v: T) => void;
  size?: Size;
  mono?: boolean | undefined;
  className?: string | undefined;
}) {
  return (
    <div
      role="radiogroup"
      className={cn(
        "inline-flex items-center rounded-md border border-border bg-surface p-0.5",
        className,
      )}
    >
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={o.disabled}
            title={o.title}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-[5px] transition-colors",
              "disabled:cursor-not-allowed disabled:opacity-45",
              focusRing,
              itemSize[size],
              mono && "font-mono",
              active ? "bg-surface-elevated text-ink" : "text-ink-muted hover:text-ink",
            )}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
